import React, { useContext, useState, useCallback, useMemo, useRef } from 'react';
import { Container, Table, Button, Row, Col, Modal, Form, Spinner } from 'react-bootstrap';
import { IdentityContext } from '../../../../../../context/identity-context';
import { UploadModal, CreateReportModal, CommentModal, ExcelPreviewModal } from './components/modal/modal';
import ReportsService from '../../../../../../services/reports-service';
import * as XLSX from 'xlsx';
import './event-based-report-container.scss';

const MAX_PREVIEW_ROWS = 500; // limit rows for performance; preview only first N if larger

const EventBasedReportContainer = ({ items, handleCheckboxChange }) => {
  const reportService = new ReportsService();
  const { user } = useContext(IdentityContext);

  const [createModalShow, setCreateModalShow] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [uploadModalData, setUploadModalData] = useState({
    show: false,
    report: null,
    item: null,
    file: null,
  });
  const [commentModalData, setCommentModalData] = useState({ show: false, comment: '' });
  const [formData, setFormData] = useState({
    reportTitle: '',
    issuer: '',
    isin: '',
    file: '',
  });
  const createModalRef = useRef(null);
  const [submissionModalShow, setSubmissionModalShow] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState('');

  // New search term state
  const [searchTerm, setSearchTerm] = useState('');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Excel preview states
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Error modal state
  const [errorModalShow, setErrorModalShow] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // toggle direction
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // new sort key, default to ascending
        return { key, direction: 'asc' };
      }
    });
  };

  const handleCreateReport = () => {
    setFormData({ reportTitle: '', issuer: '', isin: '', file: '' });
    setCreateModalShow(true);
  };

  const handleCreateModalClose = () => {
    setCreateModalShow(false);
  };

  const handleCloseSubmissionModal = () => {
    setSubmissionModalShow(false);
    window.location.reload();
  };

  const showError = (message) => {
    setErrorModalMessage(message);
    setErrorModalShow(true);
  };

  const handleSubmitCorrected = async () => {
    const checkedReports = items.filter(item => selectedItems.includes(item.id));
    console.log('Checked reports:', checkedReports);

    const totalReports = checkedReports.length;
    const makerName = user.attributes['custom:display_name'];

    if (totalReports === 0) {
      setSubmissionMessage("No reports selected to send.");
      setSubmissionModalShow(true);
      return;
    }

    const submissionPromises = checkedReports.map(report => {
      const options = {
        issuer: report.securityIssuer,
        bpId: report.bpId || '',
        holder: report.holder || ''
      };
      const reportTitleLower = report.reportTitle.toLowerCase();

      if (reportTitleLower.includes('bir')) {
        const reportDate = {
          dateFrom: report.dateFrom,
          dateTo: report.dateTo,
          paymentDate: ''
        };
        return reportService.makerSubmitEventBasedReport(
          report.isin,
          report.reportTitle,
          options,
          reportDate,
          null,
          makerName
        );
      } else if (reportTitleLower.includes('closure')) {
        const reportDate = {
          dateFrom: '',
          dateTo: '',
          paymentDate: report.paymentDate,
          maturityDate: report.maturityDate
        };
        return reportService.makerSubmitEventBasedReport(
          report.isin,
          report.reportTitle,
          options,
          reportDate,
          null,
          makerName
        );
      } else if (reportTitleLower.includes('consent')) {
        const reportDate = {
          dateFrom: '',
          dateTo: '',
          paymentDate: report.paymentDate
        };
        const opts = {
          address: report.address,
          issuer: report.securityIssuer,
          amount: report.amount
        };
        return reportService.makerSubmitEventBasedReport(
          report.isin,
          report.reportTitle,
          opts,
          reportDate,
          null,
          makerName
        );
      } else if (reportTitleLower.includes('credit')) {
        return reportService.makerSubmitEventBasedCreditLetterReport(
          report.isin,
          report.reportTitle,
          report.securityIssuer,
          report.paymentDate,
          report.address,
          makerName
        );
      } else {
        return Promise.reject(new Error('Unknown report type'));
      }
    });

    try {
      const results = await Promise.allSettled(submissionPromises);
      const successCount = results.filter(r => r.status === "fulfilled").length;
      const failureCount = totalReports - successCount;

      let message = "";
      if (successCount === 0) {
        const reasons = results
          .map((r, idx) => {
            if (r.status === "rejected") {
              const rpt = checkedReports[idx];
              const reason = r.reason?.message || r.reason;
              return `${rpt.reportTitle}: ${reason}`;
            }
            return null;
          })
          .filter(x => x !== null);
        message = `Failed to send any of the ${totalReports} reports.` 
          + (reasons.length
              ? ` Reasons: ${reasons.join('; ')}.`
              : "");
      } else if (successCount === totalReports) {
        message = `Successfully sent all ${totalReports} report${totalReports > 1 ? 's' : ''}.`;
      } else {
        const failedDetails = results
          .map((r, idx) => {
            if (r.status === "rejected") {
              const rpt = checkedReports[idx];
              const reason = r.reason?.message || r.reason;
              return `${rpt.reportTitle}: ${reason}`;
            }
            return null;
          })
          .filter(x => x !== null);
        message = `Sent ${successCount} of ${totalReports} reports; ${failureCount} failed.` 
          + (failedDetails.length
              ? ` Failed: ${failedDetails.join('; ')}.`
              : "");
      }

      setSubmissionMessage(message);
      setSubmissionModalShow(true);
    } catch (err) {
      console.error('Unexpected error in sending reports:', err);
      showError("An unexpected error occurred. Please try again.");
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  };

  const handleSubmitReport = (report) => {
    const makerName = user.attributes['custom:display_name'];
  
    return new Promise((resolve, reject) => {
      const finalize = () => {
        setCreateModalShow(false);
        resolve(); // notify parent
        window.location.reload(); // then reload
      };
  
      if (report.reportTitle === 'Credit Letter') {
        reportService
          .makerSubmitEventBasedCreditLetterReport(
            report.isin,
            report.reportTitle,
            report.issuer,
            report.paymentDate,
            report.address,
            makerName
          )
          .then(finalize)
          .catch((err) => {
          // pull out your server’s error field if it exists
            const msg = err?.error || err?.response?.data?.error || err.message || 'An unexpected error occurred.';
            showError(msg);
          });
      } else {
        const reader = new FileReader();
        reader.readAsDataURL(report.file);
        reader.onloadend = async () => {
          const base64File = reader.result.split(",").pop();
          try {
            if (report.reportTitle === 'Consent Solicitation Entitlement Report Excel') {
              await reportService.makerSubmitEventBasedConsentSolicitationReport(
                report.isin,
                report.reportTitle,
                report.issuer,
                report.address,
                report.paymentDate,
                report.amount,
                base64File,
                makerName,
                report.file.name
              );
            } else {
              const options = { issuer: report.issuer, bpId: report.bpId || '', holder: report.holder || '' };
              const reportDate = { dateFrom: report.dateFrom, dateTo: report.dateTo, paymentDate: report.paymentDate || '', maturityDate: report.maturityDate };
              await reportService.makerSubmitEventBasedReport(
                report.isin,
                report.reportTitle,
                options,
                reportDate,
                base64File,
                makerName,
                report.file.name
              );
            }
            finalize();
          } catch (error) {
            reject(error);
            const msg = error?.error || error?.response?.data?.error || error.message || 'An unexpected error occurred.';
            showError(msg);
          }
        };
        reader.onerror = (err) => {
          showError(err.message || 'File read error');
        };
      }
    });
  };

  const handleUploadModalOpen = useCallback((item) => {
    setUploadModalData({ show: true, report: null, item });
  }, []);

  const handleUploadModalClose = useCallback(() => {
    setUploadModalData({ show: false });
  }, []);

  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      // Additional processing can be added here if needed
    } catch (e) {
      console.log(e);
    }
    return url;
  };

  const areReportsSelected = selectedItems.length > 0;

  // Helper: sanitize S3 key parts by replacing slashes to avoid unintended path segments
  const sanitizeKeyPart = (str) => {
    if (!str) return '';
    return String(str).replace(/\//g, '-');
  };

  // Build S3 prefix based on report fields, with clearer delimiter for dateFrom_dateTo
  const buildS3Prefix = (report) => {
    const title = sanitizeKeyPart(report.reportTitle);
    const isin = sanitizeKeyPart(report.isin);
    if (
      report.reportTitle.startsWith('Closure of Registry Service') ||
      report.reportTitle.startsWith('Consent Solicitation Entitlement') ||
      report.reportTitle.startsWith('Credit Letter')
    ) {
      const paymentDate = sanitizeKeyPart(report.paymentDate);
      return `Ad-hoc Reports/${isin}/${title}/${paymentDate}/`;
    } else {
      const bpId = sanitizeKeyPart(report.bpId || '');
      const holder = sanitizeKeyPart(report.holder || '');
      const df = sanitizeKeyPart(report.dateFrom || '');
      const dt = sanitizeKeyPart(report.dateTo || '');
      const dateRange = df || dt ? `${df}${dt}` : '';
      return `Ad-hoc Reports/${isin}/${title}/${bpId}/${holder}/${dateRange}/`;
    }
  };

  // Determine if file is Excel by extension or content-type (if available)
  const isExcelByExtension = (report, fileURL) => {
    const urlObj = new URL(fileURL);
    const urlLower = urlObj.pathname.toLowerCase();
    return (
      report.reportTitle.startsWith('Consent Solicitation Entitlement Excel') ||
      urlLower.endsWith('.xlsx') ||
      urlLower.endsWith('.xls')
    );
  };

  // Fetch workbook: returns XLSX.WorkBook or throws
  const fetchWorkbook = async (fileURL) => {
    const fileResponse = await fetch(fileURL);
    if (!fileResponse.ok) {
      throw new Error(`Network response was not ok (${fileResponse.status})`);
    }
    // Check content-type header if present
    const contentType = fileResponse.headers.get('Content-Type') || '';
    const blob = await fileResponse.blob();
    const arrayBuffer = await blob.arrayBuffer();

    // Attempt parse as Excel
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('No sheets found in workbook');
      }
      return workbook;
    } catch (parseErr) {
      console.error('Error parsing Excel:', parseErr);
      throw new Error('Failed to parse Excel file');
    }
  };

  // Normalize sheet to 2D array of { value, readOnly: true }, limiting rows if large
  const normalizeSheet = (sheet) => {
    const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const totalRows = jsonData.length;
    let usedRows = jsonData;
    if (totalRows > MAX_PREVIEW_ROWS) {
      usedRows = jsonData.slice(0, MAX_PREVIEW_ROWS);
      console.warn(`Preview limited to first ${MAX_PREVIEW_ROWS} rows out of ${totalRows}`);
      // The modal component could show a note if desired.
    }
    const maxCols = usedRows.reduce((max, row) => Math.max(max, row.length), 0);
    const normalizedData = usedRows.map(row =>
      Array.from({ length: maxCols }, (_, i) => (row[i] !== undefined ? row[i] : ''))
    );
    return normalizedData.map(row =>
      row.map(cell => ({ value: cell, readOnly: true }))
    );
  };

  const handlePreviewClick = async (report) => {
    setPreviewLoading(true);
    try {
      const s3Prefix = buildS3Prefix(report);
      const response = await reportService.retrieveEventBasedReport(s3Prefix);
      const fileURL = response.fileURL;
      if (!fileURL) {
        throw new Error('No file URL returned');
      }

      const byExt = isExcelByExtension(report, fileURL);

      if (byExt) {
        let workbook;
        try {
          workbook = await fetchWorkbook(fileURL);
        } catch (err) {
          console.error('Excel fetch/parse failed, falling back to non-Excel preview:', err);
          const embedUrl = getEmbedUrl(fileURL);
          const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
          return;
        }
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = normalizeSheet(sheet);
        setExcelPreviewData(data);
        setShowExcelPreviewModal(true);
      } else {
        const embedUrl = getEmbedUrl(fileURL);
        const newWindow = window.open(embedUrl, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error fetching report preview:', error);
      showError('Failed to load preview: ' + (error.message || error));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLocalCheckboxChange = (id) => (e) => {
    if (e.target.checked) {
      setSelectedItems((prev) => [...prev, id]);
    } else {
      setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
    }
    if (handleCheckboxChange) {
      handleCheckboxChange(id);
    }
  };

  const handleDownloadClick = async (report) => {
    try {
      const s3Prefix = buildS3Prefix(report);
      const response = await reportService.retrieveEventBasedReport(s3Prefix);

      if (response && response.fileURL) {
        const url = response.fileURL;
        const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);

        const fileResponse = await fetch(url);
        if (!fileResponse.ok) throw new Error('Network response was not ok');

        const blob = await fileResponse.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        console.error('Download: No file URL returned');
        showError('Failed to download file: No file URL returned');
      }
    } catch (error) {
      console.error('Error fetching report download:', error);
      showError('Failed to download file: ' + (error.message || error));
    }
  };

  const handleUploadClick = async (report) => {
    // Existing functionality for upload (if any) goes here.
  };

  // Create a derived filtered list from the search term.
  const filteredItems = items.filter(item => {
    const searchableText = `
      ${item.reportTitle} 
      ${item.securityIssuer} 
      ${item.isin} 
      ${item.status} 
      ${item.bpId || ''} 
      ${item.maturityDate || ''} 
      ${item.holder || ''}
      ${item.dateSubmitted}
    `.toLowerCase();
    return searchableText.includes(searchTerm.toLowerCase());
  });

  // Derive sorted + filtered list
  const sortedItems = useMemo(() => {
    if (!sortConfig.key) {
      return filteredItems;
    }
    const sorted = [...filteredItems];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      if (sortConfig.key === 'dateSubmitted') {
        const da = new Date(aValue);
        const db = new Date(bValue);
        if (!isNaN(da) && !isNaN(db)) {
          aValue = da;
          bValue = db;
        }
      }
      if (aValue instanceof Date && bValue instanceof Date) {
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }
      const aStr = aValue ? String(aValue) : '';
      const bStr = bValue ? String(bValue) : '';
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  return (
    <Container
      fluid
      className="report-container event-based-report-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '37.5rem'
      }}
    >
      {/* Search bar */}
      <Row className="mb-3">
        <Col md={12}>
          <Form.Control
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col>
          {/* <h5>Event-Based Report</h5> */}
        </Col>
        <Col className="text-right">
          <Button variant="primary" onClick={handleCreateReport}>
            Create Report
          </Button>
        </Col>
      </Row>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Table bordered>
          <thead>
            <tr>
              <th></th>
              <th>Report Name</th>
              <th>Issuer</th>
              <th>ISIN</th>
              <th>Status</th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('dateSubmitted')}
              >
                Date Submitted{' '}
                {sortConfig.key === 'dateSubmitted' && (
                  sortConfig.direction === 'asc' ? '▲' : '▼'
                )}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {sortedItems && sortedItems.length > 0 ? (
              sortedItems.map((item, index) => (
                <tr key={index}>
                  <td>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        style={{ marginRight: '8px' }}
                        disabled={!(item.status === 'Report Available' || item.status === 'For Adjustment')}
                        checked={selectedItems.includes(item.id)}
                        onChange={handleLocalCheckboxChange(item.id)}
                      />
                    </label>
                  </td>
                  <td>
                    {item.reportTitle === 'BIR 2306 Request Letter' ? (
                      <>
                        {'BIR 2306 Request Letter'}<br />
                        {`BPID: ${item.bpId || ''}`}<br />
                        {`Holder Name: ${item.holder || ''}`}
                      </>
                    ) : item.reportTitle === 'Closure of Registry Service / Payment Account' ? (
                      <>
                        {'Closure of Registry Service / Payment Account'}<br />
                        {`Maturity Date: ${item.maturityDate || ''}`}
                      </>
                    ) : item.reportTitle === 'Credit Letter' ? (
                      <>
                        {'Credit Letter'}<br />
                        {`Payment Date: ${item.paymentDate || ''}`}
                      </>
                    ) : (
                      item.reportTitle
                    )}
                  </td>
                  <td>{item.securityIssuer}</td>
                  <td>{item.isin}</td>
                  <td>
                    {item.status}
                    {item.comment && (
                      <span
                        style={{ cursor: 'pointer', marginLeft: '5px' }}
                        onClick={() => setCommentModalData({ show: true, comment: item.comment })}
                      >
                        &#9865;
                      </span>
                    )}
                  </td>
                  <td>
                    {item.dateSubmitted}
                  </td>
                  <td>
                    {item.status === 'For Adjustment' && (
                      <Button variant="primary" className="action-button" onClick={() => handleUploadModalOpen(item)}>
                        Upload
                      </Button>
                    )}
                    {item.status !== 'For Generation' && (
                      <>
                        <Button
                          variant="primary"
                          className="action-button"
                          onClick={() => handlePreviewClick(item)}
                          disabled={previewLoading}
                        >
                          {previewLoading ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Previewing...
                            </>
                          ) : (
                            'Preview'
                          )}
                        </Button>
                        <Button variant="primary" className="action-button" onClick={() => handleDownloadClick(item)}>
                          Download
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">
                  No Reports
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Row>
        <Col xs={12}>
          <div className="actions-container">
            <Button
              onClick={handleSubmitCorrected}
              className="submit-button"
              variant="primary"
              disabled={!areReportsSelected}
              aria-disabled={!areReportsSelected}
            >
              Submit Corrected Reports
            </Button>
          </div>
        </Col>
      </Row>

      <CreateReportModal
        ref={createModalRef}
        show={createModalShow}
        onClose={handleCreateModalClose}
        onSubmit={handleSubmitReport}
        formData={formData}
        setFormData={setFormData}
      />

      <UploadModal
        report={uploadModalData.report}
        item={uploadModalData.item}
        show={uploadModalData.show}
        onClose={handleUploadModalClose}
      />

      <Modal show={submissionModalShow} onHide={handleCloseSubmissionModal} dialogClassName="submission-modal" >
        <Modal.Header closeButton>
          <Modal.Title>Submission Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>{submissionMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseSubmissionModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <CommentModal
        show={commentModalData.show}
        comment={commentModalData.comment}
        onClose={() => setCommentModalData({ show: false, comment: '' })}
      />

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => setShowExcelPreviewModal(false)}
        />
      )}

      {/* Error Modal */}
      <Modal show={errorModalShow} onHide={() => setErrorModalShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorModalMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setErrorModalShow(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EventBasedReportContainer;
